import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useParams, useNavigate } from 'react-router';
import { Divider } from 'ui-kit/Divider';
import Check from 'assets/check.svg';
import { Step } from 'components/NewRelease/constants';
import { getNewReleaseSubMenu } from "./constants";
import { getReadyStatus } from "./utils";
import { FormattedMessage } from "react-intl";
import { useRelease } from 'components/NewRelease/hooks/useRelease';
import { runDraftSaveIfNeeded } from "components/NewRelease/draftSaveGuard";

interface SubMenuItem {
  id: Step;
  label: string;
  path: {
    pathname: string;
    search: string;
  };
  subMenu?: {
    label: string;
    path: string;
  }[];
}

export const NewReleaseSubMenu: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search } = location;

  const newReleaseSubMenu = React.useMemo(() => getNewReleaseSubMenu(location), [location]);
  const { release, fetchRelease } = useRelease()

  const currentStepId = React.useMemo(() => {
    const qp = new URLSearchParams(search);
    return (qp.get("step") as Step) || Step.Type;
  }, [search]);

  const currentStepIndex = React.useMemo(() => {
    const idx = newReleaseSubMenu.findIndex((m: any) => m.id === currentStepId);
    return idx >= 0 ? idx : 0;
  }, [newReleaseSubMenu, currentStepId]);

  const isStepCompleted = React.useCallback((step: Step) => {
    const r: any = release ?? {};
    switch (step) {
      case Step.Type:
        return !!r?.release_type_id || !!r?.release_type;
      case Step.UploadImage:
        return !!String(r?.cover_url ?? "").trim();
      case Step.ReleaseInfo:
        return (
          !!String(r?.title ?? "").trim() &&
          Array.isArray(r?.artists) && r.artists.length > 0 &&
          (r?.label_id != null || r?.label?.id != null) &&
          (r?.main_genre != null || (Array.isArray(r?.genres) && r.genres.some((g: any) => g?.is_primary))) &&
          !!String(r?.release_date ?? "").trim()
        );
      case Step.TrackUpload:
      case Step.TrackInfo: {
        const tracks = Array.isArray(r?.tracks) ? r.tracks : [];
        if (!tracks.length) return false;
        // Completion for moving past track steps:
        // require ALL tracks to have audio (otherwise user can jump to Publish/Review without uploading).
        return tracks.every((t: any) => t?.has_audio === true || t?.has_audio === 1 || t?.has_audio === "1" || !!t?.audio_url);
      }
      case Step.Publish: {
        const platformsOk = Array.isArray(r?.platforms) && r.platforms.length > 0;
        const countriesOk = Array.isArray(r?.countries) && r.countries.length > 0;
        return platformsOk && countriesOk;
      }
      case Step.Platform:
      default:
        return true;
    }
  }, [release]);

  // Allow navigation to any step up to the first incomplete step (sequential unlock).
  // This preserves strict gating for new releases, but allows jumping on already-filled releases.
  const furthestUnlockedIndex = React.useMemo(() => {
    let unlocked = 0;
    for (let i = 0; i < newReleaseSubMenu.length; i++) {
      const step = newReleaseSubMenu[i]?.id as Step | undefined;
      if (!step) break;
      if (!isStepCompleted(step)) break;
      unlocked = i;
    }
    return unlocked;
  }, [newReleaseSubMenu, isStepCompleted]);

  const canNavigateToIndex = React.useCallback((targetIndex: number) => {
    // Always allow staying on current step
    if (targetIndex === currentStepIndex) return true;
    // Allow going back freely
    if (targetIndex < currentStepIndex) return true;
    // Allow going forward only within unlocked range
    return targetIndex <= furthestUnlockedIndex;
  }, [currentStepIndex, furthestUnlockedIndex]);

  useEffect(() => {
    fetchRelease({ backgroundRefresh: true }).catch(() => {
      // useRelease already sets `error`; avoid unhandled promise rejection noise
    });
  }, [search, fetchRelease])

  

  

  return (
    <Container>
      <Divider $margin={20} />
      <Title>
        <FormattedMessage
          id="MenuItem.Releases.CreateNewRelease"
          defaultMessage="Create a new release"
          description="Create a new release"
        />
      </Title>
      <Items>
        {newReleaseSubMenu.map((menuItem: SubMenuItem, index) => {
          const isActive = menuItem.path.pathname === pathname && menuItem.path.search === search;
          const isDisabled = !canNavigateToIndex(index);
        
          

          const onClick = (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            if (!isDisabled) {
              (async () => {
                try {
                  await runDraftSaveIfNeeded();
              navigate(menuItem.path, { replace: true });
                } catch (err) {
                  // If save fails, stay on current step (better than losing changes)
                  console.error("[NewReleaseSubMenu] draft save failed; navigation cancelled", err);
                }
              })();
            }
          };

          return (
            <MenuItemContainer key={index}>
              <MenuItem
                key={menuItem.id}
                $isActive={isActive}
                $isDisabled={isDisabled}
                onClick={onClick}
              >
                {menuItem.label}
                <IconContainer $isReady={getReadyStatus(menuItem.id, search)}>
                  <Check />
                </IconContainer>
              </MenuItem>

              {!!menuItem.subMenu && menuItem.subMenu.length > 0 && isActive && (
                <MenuItemSubMenu>
                  {menuItem.subMenu.map(item => {
                    const onClick = (e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();

                      (async () => {
                        try {
                          await runDraftSaveIfNeeded();
                      navigate({ ...menuItem.path, hash: item.path }, { replace: true });
                        } catch (err) {
                          console.error("[NewReleaseSubMenu] draft save failed; navigation cancelled", err);
                        }
                      })();
                    };
                    const hash = location.hash || '';
                    const isActive = hash === item.path;
                    return (
                      <MenuItemSubMenuItem key={item.path} onClick={onClick} $isActive={isActive}>
                        {item.label}
                      </MenuItemSubMenuItem>
                    );
                  })}
                </MenuItemSubMenu>
              )}
            </MenuItemContainer>
          );
        })}
      </Items>
      <Divider $margin={20} />
    </Container>
  );
};

const Container = styled.div`
  &::-webkit-scrollbar {
    display: none; 
  }
`;

const Items = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 450px;
  overflow-y: auto;
  overflow-x: hidden;
  &::-webkit-scrollbar {
    display: none; 
  }
`;

const MenuItem = styled.div<{ $isActive: boolean; $isDisabled: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 40px;
  width: calc(270px - 13px);
  padding: 10px 15px;
  border-radius: 10px;
  font-weight: ${props => props.$isActive ? 600 : 400};
  font-size: 17px;
  line-height: 17px;
  text-decoration: none;
  cursor: pointer;
  background-color: ${props => props.$isActive ? '#E2E5FF' : '#FFFFFF'};
  color: ${props => props.$isActive ? 'rgba(0, 0, 0, 1)' : 'rgba(0, 0, 0, 0.5)'};
  
  ${props => !props.$isDisabled && `
     &:hover {
       background-color: #E2E5FF;
     }
   `}
  
  ${props => props.$isDisabled && `
    pointer-events: none;
    cursor: not-allowed;
    opacity: 0.5;
  `}
`;

const IconContainer = styled.div<{ $isReady: boolean }>`
  opacity: ${props => props.$isReady ? 1 : 0.5};
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 20px;
  line-height: 20px;
  color: #000000;
  margin-bottom: 26px;
`;

const MenuItemSubMenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 235px;
  padding-left: 10px;
  border-left: 1px solid rgba(0, 0, 0, 0.2);
`;

const MenuItemSubMenuItem = styled.div<{ $isActive: boolean }>`
  height: 40px;
  border-radius: 10px;
  padding: 10px 16px;
  background-color: ${props => props.$isActive ? '#E2E5FF' : 'transparent'};
  
  &:hover {
    background-color: #E2E5FF;
    cursor: pointer;
  }
`;

const MenuItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 20px;
`;
